import type { SceneListItem, TreeGraph } from "@chuzi/shared/types";

export function sortScenesForFlow(sceneList: SceneListItem[]): SceneListItem[] {
  return [...sceneList].sort((a, b) => {
    if (a.is_title !== b.is_title) return a.is_title ? -1 : 1;
    if (a.order !== b.order) return a.order - b.order;
    return a.created_at.localeCompare(b.created_at);
  });
}

/** Spine order from tree_graph (level → branch x → index), not editor list order. */
export function orderScenesForConstellation(
  sceneList: SceneListItem[],
  graph: TreeGraph | undefined,
): SceneListItem[] {
  if (!graph?.nodes?.length) return sortScenesForFlow(sceneList);

  const sceneById = new Map(sceneList.map((scene) => [scene.id, scene]));
  const ordered: SceneListItem[] = [];
  const seen = new Set<string>();

  const sortedNodes = [...graph.nodes].sort((a, b) => {
    if (a.level !== b.level) return a.level - b.level;
    if (a.x !== b.x) return a.x - b.x;
    return a.index - b.index;
  });

  for (const node of sortedNodes) {
    const scene = sceneById.get(node.id);
    if (scene && !seen.has(scene.id)) {
      ordered.push(scene);
      seen.add(scene.id);
    }
  }

  for (const scene of sortScenesForFlow(sceneList)) {
    if (!seen.has(scene.id)) {
      ordered.push(scene);
      seen.add(scene.id);
    }
  }

  return ordered;
}

function hashSceneId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  return h / 4294967296;
}

function hexToHue(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return Math.round(h * 360);
}

const SCENE_STAR_HUES = [355, 18, 42, 85, 205, 262, 320];

export function resolveSceneStarHue(
  scene: SceneListItem,
  fallbackHue: number,
): number {
  const hex = scene.color?.trim();
  if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) {
    return hexToHue(hex);
  }
  return (
    SCENE_STAR_HUES[
      Math.floor(hashSceneId(scene.id) * SCENE_STAR_HUES.length) %
        SCENE_STAR_HUES.length
    ] ?? fallbackHue
  );
}
